import {
  ArrowLeftIcon,
  ArrowRightIcon,
  DownloadIcon,
} from '@radix-ui/react-icons';
import {
  Badge,
  Box,
  Button,
  Card,
  ChevronDownIcon,
  Flex,
  Heading,
  SegmentedControl,
  Skeleton,
} from '@radix-ui/themes';
import { FileHelper } from 'classes/helpers/file.helper';
import { NotifyHelper } from 'classes/helpers/notify.helper';
import { BlurOverlay } from 'components/common/blur-overlay';
import { CommonBackArrow } from 'components/common/buttons/back-arrow';
import { CommonCallout } from 'components/common/callouts';
import { DialogButton } from 'components/common/dialogs/button';
import FloatingCopyButton from 'components/common/form/floating-copy-button';
import { JsonViewer } from 'components/common/json/json-viewer';
import { CommonMenu } from 'components/common/menu';
import { CommonTabs } from 'components/common/tabs';
import { CommonTooltip } from 'components/common/tooltip';
import { CommonVideoPlayerHoC } from 'components/common/video/player';
import { formatDate } from 'components/sections/admin-portal/machines/dialogs/settings/machine-shot-history/common';
import { VideoPlayerType } from 'enums/video-player.enums';
import { IZipFile } from 'interfaces/forms/file';
import { IMenuAction } from 'interfaces/i-menus';
import { S3Command } from 'lib_ts/enums/data.enums';
import { ImageMime } from 'lib_ts/enums/files.enums';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { AwsRegion } from 'lib_ts/enums/s3.enum';
import { StereoVideoType } from 'lib_ts/enums/videos.enums';
import { Nullable } from 'lib_ts/interfaces/common/i-generics';
import { IFireEvent } from 'lib_ts/interfaces/i-session-event';
import { IStereoVideo } from 'lib_ts/interfaces/i-stereo-video';
import { IVideoPlayback } from 'lib_ts/interfaces/i-video';
import { IMissedVisionDetection } from 'lib_ts/interfaces/vision/i-vision-detection';
import { first } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { SessionEventsService } from 'services/session-events.service';
import { StereoVideosService } from 'services/stereo-videos.service';
import useSignedUrlsStore from 'stores/use-signed-urls-store';

interface IMissedDetectionDetailsProps {
  missedDetection: IMissedVisionDetection;
  hasPrev: boolean;
  hasNext: boolean;
  loadingPrev: boolean;
  onBack: () => void;
  onPrev: () => void;
  onNext: () => void;
}

enum MissedDetectionDetailsTabKey {
  MissedDetection = 'Missed Detection',
  Fire = 'Fire',
}

enum MissedDetectionAssetType {
  DebugImage = 'Debug Image',
  RawVideo = 'Raw Video',
}

const DownloadVideosButton = (props: {
  fireID: string;
  trajVideo?: Nullable<IStereoVideo>;
  rawVideo?: Nullable<IStereoVideo>;
  spinsVideo?: Nullable<IStereoVideo>;
  ready: boolean;
}) => {
  const [loading, setLoading] = useState(false);

  const onClick = useCallback(
    async (videos: { name: string; video: IStereoVideo }[]) => {
      setLoading(true);

      try {
        const validVideos = videos.filter((m) => !!m.video.path);

        const signed: IZipFile[] = await Promise.all(
          validVideos.map(async (m) => ({
            name: m.name,
            url: await useSignedUrlsStore
              .getState()
              .getSignedUrl(
                m.video.region,
                m.video.path as string,
                S3Command.getObject,
                undefined,
                true
              ),
          }))
        );

        // just in case we fail to sign something
        const signedVideos = signed.filter((video) => !!video.url);

        if (signedVideos.length === 0) {
          return;
        }

        // no need to zip if we're only downloading one video
        if (signedVideos.length === 1) {
          window.open(signedVideos[0]?.url, '_blank');
          return;
        }

        await FileHelper.zipFiles({
          fileName: `${props.fireID} Videos`,
          files: signedVideos.map((video) => {
            // FW wants 'spin' instead of 'spins' in the filename
            const typeSuffix =
              video.name === StereoVideoType.Spins ? 'spin' : video.name;

            const name = `${props.fireID}-${typeSuffix}`;
            return {
              name,
              url: video.url as string,
            };
          }),
        });
      } catch (error) {
        console.error('Error downloading files:', error);
        NotifyHelper.error({
          message_md:
            'We were unable to download the videos. Please try again later.',
        });
      } finally {
        setLoading(false);
      }
    },
    [props.fireID]
  );

  const actions = useMemo<IMenuAction[]>(() => {
    const acts: IMenuAction[] = [];
    if (props.rawVideo) {
      acts.push({
        label: 'Download Raw Video',
        onClick: () => {
          if (!props.rawVideo) {
            return;
          }

          onClick([
            {
              name: StereoVideoType.Raw,
              video: props.rawVideo,
            },
          ]);
        },
      });
    }

    if (props.spinsVideo) {
      acts.push({
        label: 'Download Spin Video',
        onClick: () => {
          if (!props.spinsVideo) {
            return;
          }

          onClick([
            {
              name: StereoVideoType.Spins,
              video: props.spinsVideo,
            },
          ]);
        },
      });
    }

    return acts;
  }, [props.trajVideo, props.rawVideo, props.spinsVideo]);

  // no videos
  if (!actions.length) {
    return null;
  }

  if (actions.length === 1) {
    const action = first(actions);
    return (
      <Box position="absolute" right="0">
        <DialogButton
          onClick={action?.onClick}
          label="Download Video"
          icon={<DownloadIcon />}
          variant="solid"
          loading={loading}
        />
      </Box>
    );
  }

  return (
    <CommonMenu
      title="Download Videos"
      side="bottom"
      trigger={
        <Button loading={loading}>
          Download Videos <ChevronDownIcon />
        </Button>
      }
      actions={actions}
      skipSort
    />
  );
};

export const MissedDetectionDetails = (props: IMissedDetectionDetailsProps) => {
  const { missedDetection, hasNext, hasPrev, onBack } = props;
  const { debug_img_s3_path, fireEventID } = missedDetection;

  const [loading, setLoading] = useState(false);

  const [videoData, setVideoData] = useState<
    | {
        record: IStereoVideo;
        playback: IVideoPlayback;
      }
    | undefined
  >();

  // try and fetch raw and spins videos if they exist
  const [rawVideo, setRawVideo] = useState<IStereoVideo>();
  const [spinsVideo, setSpinsVideo] = useState<IStereoVideo>();
  const [debugImgUrl, setDebugImgUrl] = useState<string>('');

  const [stereoVideosLoaded, setStereoVideosLoaded] = useState(false);
  const [fire, setFire] = useState<IFireEvent>();

  const [activeTab, setActiveTab] = useState<MissedDetectionDetailsTabKey>(
    MissedDetectionDetailsTabKey.Fire
  );

  const [assetType, setAssetType] = useState<MissedDetectionAssetType>(
    MissedDetectionAssetType.DebugImage
  );

  const getVideoPlayback = useCallback(
    async (video?: IStereoVideo) => {
      if (!video?.path) {
        setVideoData(undefined);
        return;
      }

      const url = await useSignedUrlsStore
        .getState()
        .getSignedUrl(
          video.region,
          video.path,
          S3Command.getObject,
          undefined,
          true
        );

      if (!url) {
        setVideoData(undefined);
        return;
      }

      setVideoData({
        record: video,
        playback: {
          video: {
            url,
            mime_type: video.mime,
            speed: video.speed,
            cap_size_0: video.height,
            cap_size_1: video.width,
          },
          thumb: {
            url: '',
            mime_type: ImageMime.None,
          },
        },
      });
    },
    [setVideoData]
  );

  useEffect(() => {
    const getFire = async () => {
      const result = await SessionEventsService.getFireByID(fireEventID);
      setFire(result);
      return result;
    };

    const getRawVideo = async (stereoVideoID: string) => {
      const result =
        await StereoVideosService.getInstance().getOne(stereoVideoID);

      if (!result) {
        return;
      }

      setRawVideo(result);
      getVideoPlayback(result);
    };

    const getSpinsVideo = async (stereoVideoID: string) => {
      const result =
        await StereoVideosService.getInstance().getOne(stereoVideoID);

      setSpinsVideo(result);
    };

    const init = async () => {
      setLoading(true);

      const fire = await getFire();

      if (!fire) {
        setLoading(false);
        setStereoVideosLoaded(true);
        return;
      }

      if (fire.raw_video_id) {
        await getRawVideo(fire.raw_video_id);
      }

      if (debug_img_s3_path) {
        const url = await useSignedUrlsStore.getState().getSignedUrl(
          AwsRegion.CA, // todo: make sure this is okay
          debug_img_s3_path,
          S3Command.getObject,
          undefined,
          true
        );

        setDebugImgUrl(url ?? '');
        setAssetType(MissedDetectionAssetType.DebugImage);
      } else {
        setDebugImgUrl('');
        setAssetType(MissedDetectionAssetType.RawVideo);
      }

      if (fire.spins_video_id) {
        await getSpinsVideo(fire.spins_video_id);
      }

      setLoading(false);
      setStereoVideosLoaded(true);
    };

    init();
  }, [fireEventID, debug_img_s3_path]);

  const showNavButtons = hasPrev || hasNext;
  const showAssetControls =
    [debug_img_s3_path, rawVideo].filter((a) => !!a).length > 1;

  return (
    <Flex direction="column" gap={RADIX.FLEX.GAP.MD}>
      <Flex gap={RADIX.FLEX.GAP.SM} align="center">
        <CommonBackArrow onClick={onBack} />
        <CommonTooltip
          text="Click to copy this missed detection ID to the clipboard"
          trigger={
            <Heading
              className="cursor-pointer"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();

                navigator.clipboard.writeText(missedDetection._id);
                NotifyHelper.success({
                  message_md: `Missed detection ID ${missedDetection._id} copied to clipboard!`,
                });
              }}
              size={RADIX.TEXT.SIZE.LG}
            >
              {missedDetection.pitch?.name ||
                `Missed Detection ${missedDetection._id}`}
            </Heading>
          }
        />
        <Badge color={RADIX.COLOR.NEUTRAL}>
          {formatDate(missedDetection._created)}
        </Badge>
        <Flex gap={RADIX.FLEX.GAP.SM} ml="auto" align="center">
          {showNavButtons && (
            <>
              <DialogButton
                icon={<ArrowLeftIcon />}
                label="Next"
                onClick={props.onNext}
                disabled={!hasNext || props.loadingPrev}
              />
              <DialogButton
                suffixIcon={<ArrowRightIcon />}
                label="Previous"
                onClick={props.onPrev}
                disabled={!hasPrev || props.loadingPrev}
                loading={props.loadingPrev}
              />
            </>
          )}
        </Flex>
      </Flex>
      <Skeleton loading={loading}>
        <Flex position="relative" direction="column" gap={RADIX.FLEX.GAP.SM}>
          {videoData && assetType === MissedDetectionAssetType.RawVideo && (
            <CommonVideoPlayerHoC
              playback={videoData.playback}
              type={VideoPlayerType.Fire}
              autoplay
            />
          )}
          <Box
            display={
              assetType === MissedDetectionAssetType.DebugImage
                ? 'block'
                : 'none'
            }
          >
            <img src={debugImgUrl} style={{ width: '100%' }} />
          </Box>
          {missedDetection.message && (
            <CommonCallout size="1" text={missedDetection.message} />
          )}
          {showAssetControls && (
            <Box position="absolute" top="24px" right="24px">
              <SegmentedControl.Root
                value={assetType}
                style={{ background: 'var(--gray-3)' }}
                onValueChange={(v) => {
                  setAssetType(v as MissedDetectionAssetType);
                }}
                size="1"
              >
                {debug_img_s3_path && (
                  <SegmentedControl.Item
                    value={MissedDetectionAssetType.DebugImage}
                    className="cursor-pointer"
                  >
                    {MissedDetectionAssetType.DebugImage}
                  </SegmentedControl.Item>
                )}
                {rawVideo && (
                  <SegmentedControl.Item
                    value={MissedDetectionAssetType.RawVideo}
                    className="cursor-pointer"
                  >
                    {MissedDetectionAssetType.RawVideo}
                  </SegmentedControl.Item>
                )}
              </SegmentedControl.Root>
            </Box>
          )}
        </Flex>
      </Skeleton>
      <Flex direction="column" position="relative">
        <CommonTabs
          tabs={[
            {
              label: MissedDetectionDetailsTabKey.Fire,
              value: MissedDetectionDetailsTabKey.Fire,
              content: (
                <Skeleton loading={loading || !fire}>
                  <Box position="relative">
                    <Card>
                      <JsonViewer value={fire} />
                    </Card>
                    <FloatingCopyButton
                      value={JSON.stringify(fire, null, 2)}
                      label="Fire"
                    />
                  </Box>
                </Skeleton>
              ),
            },
            {
              label: MissedDetectionDetailsTabKey.MissedDetection,
              value: MissedDetectionDetailsTabKey.MissedDetection,
              content: (
                <Flex direction="column" gap={RADIX.FLEX.GAP.MD}>
                  <Box position="relative">
                    <Card>
                      <JsonViewer value={missedDetection} />
                    </Card>
                    <FloatingCopyButton
                      value={JSON.stringify(missedDetection, null, 2)}
                      label="Missed Detection"
                    />
                  </Box>
                </Flex>
              ),
            },
          ]}
          value={activeTab}
          onValueChange={(value) =>
            setActiveTab(value as MissedDetectionDetailsTabKey)
          }
          gap="1"
        />
        <Box
          position="absolute"
          right="0"
          display={stereoVideosLoaded ? 'block' : 'none'}
        >
          <DownloadVideosButton
            fireID={fire?._id ?? ''}
            rawVideo={rawVideo}
            spinsVideo={spinsVideo}
            ready={stereoVideosLoaded}
          />
        </Box>
        <BlurOverlay show={loading} />
      </Flex>
    </Flex>
  );
};
